/* eslint-disable react-hooks/exhaustive-deps */

import { ApolloError } from '@apollo/client';
import triggerTracking from '@blueprint/tracking';
import { Breadcrumbs, PaginationOnPageChangeEvent } from '@blueprint/ui';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box } from '@zoopla-eng/zds-components/box';
import { AuthModal } from '@zoopla/authentication/modal';
import { useBreakpointListener } from '@zoopla/dom-util';
import { Head } from '@zoopla/layout/head';
import {
    Links,
    ProductTrackingObject,
    SearchContext,
    SeoBlurb,
    ServerData
} from '@zoopla/listing-search-context';
import {
    buildCanonicalPath,
    buildQueryParamsString
} from '@zoopla/listing-search-utils';
import { internalPromoIds } from '@zoopla/listing-search/featured/properties';
import {
    FeaturedPropertyList,
    getProductImpression
} from '@zoopla/listing-search/featured/property-list';
import {
    PaginationContainer,
    SearchResultsLayoutGroup,
    StyledPagination
} from '@zoopla/listing-search/layout';
import { Listing, Listings } from '@zoopla/listing-search/listings';
import { segmentSearchEvent } from '@zoopla/listing-search/segment';
import { updatePathPageNumber } from '@zoopla/listing-search/url';
import { useSaveListing } from '@zoopla/listing-search/use-save-listing';
import {
    filtersValuesSelector,
    identifierSelector
} from '@zoopla/redux-reducer-filters';
import {
    resetResultsAsync,
    selectPageNumber,
    totalPagesSelector
} from '@zoopla/redux-reducer-search-results';
import { selectCurrentSortOrder } from '@zoopla/redux-reducer-sort-order';
import { useIsLoggedIn } from '@zoopla/redux-reducer-user';
import { EventNames, identifyUser, trackPage } from '@zoopla/tracking';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
    ComponentProps,
    FC,
    MouseEvent,
    useContext,
    useEffect,
    useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DiscoverMore } from './DiscoverMore';
import { SearchResultsHeader } from './SearchResultsHeader';
import { SortOrderDropdown } from './SearchResultsHeader/SortOrderDropdown';
import { shouldIndexPath } from './Seo/noFollow';
import { getJSONLD } from './Seo/schemaMarkup';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';

const H2 = styled('h2')<{ color?: 'white' }>(
    ({ theme: { colors }, color }) => css`
        text-align: center;
        ${color === 'white' ? `color: ${colors.white};` : null}
    `
);

const Loading: FC<React.PropsWithChildren<{ color?: 'white' }>> = ({
    color
}) => {
    if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
    }

    return (
        <H2 data-testid="loading-search-results" color={color}>
            Loading...
        </H2>
    );
};

const removeEverythingAfterHash = (path?: string) => {
    if (!path) {
        return '';
    }

    return path.replace(/#.*/, '');
};

const EmailAllAgentsWrapper = dynamic<
    ComponentProps<
        typeof import('@zoopla/listing-search/email-agents').EmailAllAgentsWrapper
    >
>(
    () =>
        import('@zoopla/listing-search/email-agents').then(
            (module) => module.EmailAllAgentsWrapper
        ),
    {
        ssr: false
    }
);

type PromoIds = typeof internalPromoIds[number];

type InternalPromoId = typeof internalPromoIds[number];

type Product =
    | {
          listingId: string;
          featuredType?: string | null;
          title?: string | null;
          price?: string | null;
          alternativeRentFrequencyLabel?: string | null;
          address?: string | null;
          branch?: { logoUrl?: string | null; name?: string | null } | null;
          listingUris?: {
              detail?: string | null;
          } | null;
          features?:
              | ({
                    iconId?: string | null;
                    content?: number | null;
                } | null)[]
              | null;
          image?: {
              src?: string | null;
              responsiveImgList?:
                  | ({ src?: string | null; width?: number | null } | null)[]
                  | null;
          } | null;
      }
    | InternalPromoId;

export type SearchResultProps = {
    regularListings: Listing[];
    extendedListings: Listing[];
    readonly featuredProperties: (Product | PromoIds)[];
    canonicalPath: string;
    searchResults?: ServerData['searchResults'];
    discoverMore?: ServerData['discoverMore'];
    breadcrumbs?: ServerData['breadcrumbs'];
    loading: boolean;
    transactionType: SeoBlurb['transactionType'];
    error?: ApolloError;
    links: Links;
};

const SearchResults: FC<React.PropsWithChildren<SearchResultProps>> = ({
    regularListings,
    extendedListings,
    featuredProperties,
    canonicalPath: canonicalPathFromServer,
    searchResults,
    breadcrumbs,
    discoverMore,
    loading,
    transactionType,
    links,
    error
}) => {
    const reduxDispatch = useDispatch();
    const router = useRouter();
    const { query, asPath, route } = router;
    const path = removeEverythingAfterHash(asPath);

    const { state, dispatch } = useContext(SearchContext);

    const {
        searchType,
        currentSearchSource,
        userAlertId,
        isEditSavedSearchBannerVisible,
        previousSavedSearchUri,
        savedSearchAndAlerts,
        favourites,
        actionAfterAuth,
        seoAccordions
    } = state;

    const isLoggedIn = useIsLoggedIn();

    const filters = useSelector(filtersValuesSelector);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const { selectedValue: identifierSelectedValue } =
        useSelector(identifierSelector);

    const totalPages = useSelector(totalPagesSelector);
    const pageNumber = useSelector(selectPageNumber);

    const [canonicalPath, setCanonicalPath] = useState(canonicalPathFromServer);
    const isBelowShowSidebarBreakpoint = useBreakpointListener(1140, 'LT');

    const [internalPromoParam, setInternalPromoParam] = useState('');

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useAuthModal();
    const [authHeader, setAuthHeader] = useState<string | undefined>(undefined);

    const authenticateSaveToggle = useSaveListing(
        dispatch,
        favourites.listings,
        isLoggedIn,
        searchResults?.analyticsTaxonomy,
        setAuthHeader,
        openAuthModal,
        'search results'
    );

    const resetActionAfterAuth = async () => {
        dispatch({
            type: 'SET_ACTION_AFTER_AUTH',
            payload: {
                action: () => null,
                eventLabel: 'save_listing'
            }
        });
    };

    useEffect(() => {
        const promoParam = buildQueryParamsString(query, 1);

        setInternalPromoParam(promoParam);
    }, [query['q']]);

    // All tracking handled in one effect
    useEffect(() => {
        if (!searchResults?.analyticsTaxonomy?.page) {
            return;
        }
        const { analyticsTaxonomy: analyticsTaxonomyFromResponse } =
            searchResults;

        const analyticsTaxonomy = {
            ...analyticsTaxonomyFromResponse,
            location: analyticsTaxonomyFromResponse.areaName
        };

        let featuredImpressions: ProductTrackingObject[] = [];

        if (!isBelowShowSidebarBreakpoint && featuredProperties.length !== 0) {
            featuredImpressions = featuredProperties
                .map((featuredProperty, index) =>
                    getProductImpression(featuredProperty, index + 1)
                )
                // Only interested in unfilled featured entries (i.e. featured product Ad entries) as
                // featured listings are already present in the analyticsEcommerce array
                .filter((impression) => impression.id === 'Unfilled');
        }

        const analyticsEcommerce = {
            currencyCode: searchResults.analyticsEcommerce.currencyCode,
            impressions: [
                ...(searchResults.analyticsEcommerce.impressions ?? []),
                ...featuredImpressions
            ]
        };

        trackPage(
            analyticsTaxonomy.page,
            analyticsTaxonomy,
            analyticsEcommerce
        );
        segmentSearchEvent({
            eventName: EventNames.SEARCH,
            analyticsTaxonomy,
            currentSearchSource,
            searchCategory: searchResults.seoBlurb.category,
            filters
        });
    }, [searchResults]);

    useEffect(() => {
        identifyUser(window);
    }, []);

    useEffect(() => {
        const uri = searchResults?.metaInfo?.canonicalUri || '';
        const newCanonicalPath = buildCanonicalPath(uri);

        setCanonicalPath(newCanonicalPath);
    }, [searchResults?.metaInfo?.canonicalUri]);

    /**
   NOTE:
   This param is for internal ads in featured property list (Map and New Home link)
   Both links start a new search using search param in results page, e.g. "q=london&radius=0"
   */
    const currentPage =
        typeof query['pn'] === 'string' ? parseInt(query['pn']) : 1;

    const handleChangePage = async (
        onPageChangeEvent: PaginationOnPageChangeEvent
    ) => {
        onPageChangeEvent.nativeEvent.preventDefault();

        if (onPageChangeEvent.targetPage === currentPage) return;

        if (onPageChangeEvent.source === 'next') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_next`
            });
        } else if (onPageChangeEvent.source === 'previous') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_back`
            });
        } else {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `page_${onPageChangeEvent.targetPage}`
            });
        }

        const targetPage = pageNumberToHref(onPageChangeEvent.targetPage);
        targetPage && router.push(targetPage);
    };

    const pageNumberToHref = (pageNumber: number): string | undefined => {
        const currentPath = router.asPath.replace('&view_type=list', '');

        if (currentPage === pageNumber) return undefined;

        return updatePathPageNumber(currentPath, pageNumber);
    };

    const onResetFilters = async (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();

        const SEARCH_SOURCE = 'facets';
        reduxDispatch(
            resetResultsAsync(
                query,
                router,
                searchType,
                SEARCH_SOURCE,
                currentSortOrder as string,
                userAlertId
            )
        );
    };

    const sortOrderDropdown = (
        <SortOrderDropdown
            excludeRadiusFromResults={extendedListings.length > 0}
            userAlertId={userAlertId}
            searchType={searchType}
            dispatch={dispatch}
        />
    );

    const authModal = (
        <AuthModal
            isShowing={isAuthModalShowing}
            close={closeAuthModal}
            actionAfterAuth={actionAfterAuth}
            resetActionAfterAuth={resetActionAfterAuth}
            heading={authHeader}
        />
    );

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <Head
                pageTitle={searchResults?.metaInfo?.title || ''}
                pageDescription={searchResults?.metaInfo?.description || ''}
                canonicalPath={canonicalPath}
                renderSocialSharingTags={true}
                index={() =>
                    shouldIndexPath(
                        asPath,
                        query,
                        searchResults?.seoBlurb?.category || '',
                        pageNumber,
                        regularListings.length,
                        extendedListings.length
                    )
                }
                schema={() =>
                    getJSONLD(
                        asPath,
                        searchResults?.metaInfo?.canonicalUri || '',
                        breadcrumbs || []
                    )
                }
            />
            <SearchResultsHeader
                previousSavedSearchUri={previousSavedSearchUri}
                savedSearchAndAlertsUri={savedSearchAndAlerts?.uri}
                userAlertId={userAlertId}
                isEditSavedSearchBannerVisible={isEditSavedSearchBannerVisible}
                pageTitle={searchResults?.title || ''}
                isLoggedIn={isLoggedIn}
                sortOrderDropdown={sortOrderDropdown}
                outcode={searchResults?.analyticsTaxonomy?.outcode}
            />
            <SearchResultsLayoutGroup>
                <FeaturedPropertyList
                    properties={featuredProperties}
                    sectionType={transactionType}
                    propertyType={seoAccordions[0]?.propertyType}
                    identifier={identifierSelectedValue as string}
                    searchParam={internalPromoParam}
                    searchIdentifier={
                        searchResults?.analyticsTaxonomy?.searchIdentifier || ''
                    }
                />
                <Listings
                    searchType={searchType}
                    analyticsTaxonomyIdentifier={
                        searchResults?.analyticsTaxonomy?.searchIdentifier || ''
                    }
                    analyticsTaxonomy={searchResults?.analyticsTaxonomy}
                    analyticsEcommerce={searchResults?.analyticsEcommerce}
                    searchCategory={searchResults?.seoBlurb?.category ?? ''}
                    filters={filters}
                    currentSearchSource={currentSearchSource}
                    error={error}
                    regularListings={regularListings}
                    extendedListings={extendedListings}
                    onResetFilters={onResetFilters}
                    favouritesListings={favourites.listings}
                    authenticateSaveToggle={authenticateSaveToggle}
                    authModal={authModal}
                    pageNumber={pageNumber}
                    showHalo={true}
                    route={route}
                />
                {regularListings.length > 0 && (
                    <PaginationContainer data-testid="pagination">
                        <StyledPagination
                            previousLabel="< Back"
                            nextLabel="Next >"
                            totalPages={totalPages}
                            currentPage={currentPage}
                            pageNumberToHref={pageNumberToHref}
                            onPageChange={handleChangePage}
                        />
                    </PaginationContainer>
                )}

                <EmailAllAgentsWrapper
                    path={path}
                    searchType={searchType}
                    location={filters?.location?.value}
                    isUserLoggedIn={isLoggedIn}
                    createAlert={links?.createAlert || ''}
                    analyticsTaxonomy={searchResults?.analyticsTaxonomy}
                />

                <Box pt="$M100">
                    <Breadcrumbs
                        data-testid="breadcrumbs"
                        data={breadcrumbs || []}
                    />
                </Box>

                <DiscoverMore
                    findAgentsUri={discoverMore?.findAgentsUri || ''}
                    housePricesUri={discoverMore?.housePricesUri || ''}
                    searchArea={
                        searchResults?.analyticsTaxonomy?.areaName || ''
                    }
                />
            </SearchResultsLayoutGroup>
        </>
    );
};

export default SearchResults;
